import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, X, ChevronUp, ChevronDown, Edit2, Trash2, Plus, Save, Users, Calculator, Eye, Settings } from 'lucide-react';

const TimesheetSettings = () => {
  const [activeTab, setActiveTab] = useState('calculation'); // 'calculation', 'work-types', or 'visibility'

  // Calculation Settings State
  const [settings, setSettings] = useState({
    night_shift_start_time: '22:00',
    night_shift_end_time: '06:00',
    long_rotation_threshold_hours: 10.00,
    overtime_threshold_hours: 8.00,
    overtime_rate_percentage: 150.00,
    discrepancy_threshold_low_minutes: 5,
    discrepancy_threshold_medium_minutes: 15,
    discrepancy_threshold_high_minutes: 30,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('');

  // Work Types State
  const [categories, setCategories] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [workSubtypes, setWorkSubtypes] = useState([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showSubtypeModal, setShowSubtypeModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editingSubtype, setEditingSubtype] = useState(null);

  // Visibility Settings State
  const [visibilitySettings, setVisibilitySettings] = useState([]);
  const [loadingVisibility, setLoadingVisibility] = useState(true);
  const [editingVisibility, setEditingVisibility] = useState({});
  const [visibilitySearch, setVisibilitySearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDays, setBulkDays] = useState('7');
  const [showBulkModal, setShowBulkModal] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchWorkTypes();
    fetchWorkSubtypes();
    fetchVisibilitySettings();
  }, []);

  const fetchVisibilitySettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/visibility-settings/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setVisibilitySettings(response.data);
      setLoadingVisibility(false);
    } catch (error) {
      console.error('Error fetching visibility settings:', error);
      setLoadingVisibility(false);
    }
  };

  const handleUpdateVisibility = async (settingId, newDays) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/visibility-settings/${settingId}/`,
        { days_visible: parseInt(newDays) },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local state
      setVisibilitySettings(prev =>
        prev.map(setting =>
          setting.id === settingId
            ? { ...setting, days_visible: parseInt(newDays) }
            : setting
        )
      );

      setMessage('Visibility settings updated successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error updating visibility setting:', error);
      setMessage('Error updating visibility setting');
      setMessageType('error');
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      setMessage('Please select at least one user');
      setMessageType('error');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const updates = selectedUsers.map(settingId =>
        axios.patch(
          `${process.env.REACT_APP_API_URL}/api/visibility-settings/${settingId}/`,
          { days_visible: parseInt(bulkDays) },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );

      await Promise.all(updates);

      // Update local state
      setVisibilitySettings(prev =>
        prev.map(setting =>
          selectedUsers.includes(setting.id)
            ? { ...setting, days_visible: parseInt(bulkDays) }
            : setting
        )
      );

      setMessage(`Successfully updated ${selectedUsers.length} user(s)`);
      setMessageType('success');
      setSelectedUsers([]);
      setShowBulkModal(false);
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error bulk updating visibility settings:', error);
      setMessage('Error updating visibility settings');
      setMessageType('error');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const filtered = visibilitySettings.filter(setting =>
        setting.user_name.toLowerCase().includes(visibilitySearch.toLowerCase()) ||
        setting.username.toLowerCase().includes(visibilitySearch.toLowerCase())
      );
      setSelectedUsers(filtered.map(s => s.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (settingId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, settingId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== settingId));
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/calculation-settings/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage('Error loading settings');
      setMessageType('error');
      setLoading(false);
    }
  };

  const fetchWorkTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const categoriesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/work-categories/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCategories(categoriesResponse.data);

      const typesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/work-types/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Transform snake_case to camelCase
      const transformedTypes = typesResponse.data.map(type => ({
        ...type,
        rateModifierType: type.rate_modifier_type,
        rateModifierValue: type.rate_modifier_value,
        requiresSubtype: type.requires_subtype,
        requiresGrouping: type.requires_grouping,
        isActive: type.is_active,
        categoryName: type.category_name,
        categoryColor: type.category_color,
        createdAt: type.created_at,
        updatedAt: type.updated_at
      }));

      setWorkTypes(transformedTypes);

      setLoadingWorkTypes(false);
    } catch (error) {
      console.error('Error fetching work types:', error);
      setLoadingWorkTypes(false);
    }
  };

  const fetchWorkSubtypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/work-subtypes/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Transform snake_case to camelCase
      const transformed = response.data.map(subtype => ({
        ...subtype,
        workType: subtype.work_type,
        isActive: subtype.is_active,
        createdAt: subtype.created_at,
        updatedAt: subtype.updated_at
      }));

      setWorkSubtypes(transformed);
    } catch (error) {
      console.error('Error fetching work subtypes:', error);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/calculation-settings/`,
        settings,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSettings(response.data);
      setMessage('Settings saved successfully!');
      setMessageType('success');
      setSaving(false);

      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Error saving settings. Please try again.');
      setMessageType('error');
      setSaving(false);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      const token = localStorage.getItem('access_token');
      if (editingCategory) {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/work-categories/${editingCategory.id}/`,
          categoryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/work-categories/`,
          categoryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchWorkTypes();
      setShowCategoryModal(false);
      setEditingCategory(null);
      setMessage('Category saved successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving category:', error);
      setMessage('Error saving category');
      setMessageType('error');
    }
  };

  const handleSaveWorkType = async (typeData) => {
    try {
      const token = localStorage.getItem('access_token');
      if (editingType) {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/work-types/${editingType.id}/`,
          typeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/work-types/`,
          typeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchWorkTypes();
      setShowTypeModal(false);
      setEditingType(null);
      setMessage('Work type saved successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving work type:', error);
      setMessage('Error saving work type');
      setMessageType('error');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/work-categories/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkTypes();
      setMessage('Category deleted successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting category:', error);
      setMessage('Error deleting category');
      setMessageType('error');
    }
  };

  const handleDeleteWorkType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this work type?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/work-types/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkTypes();
      setMessage('Work type deleted successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting work type:', error);
      setMessage('Error deleting work type');
      setMessageType('error');
    }
  };

  const handleSaveSubtype = async (subtypeData) => {
    try {
      const token = localStorage.getItem('access_token');
      if (editingSubtype) {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/work-subtypes/${editingSubtype.id}/`,
          subtypeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/work-subtypes/`,
          subtypeData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchWorkSubtypes();
      setShowSubtypeModal(false);
      setEditingSubtype(null);
      setSelectedWorkType(null);
      setMessage('Subtype saved successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving subtype:', error);
      setMessage('Error saving subtype');
      setMessageType('error');
    }
  };

  const handleDeleteSubtype = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subtype?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/work-subtypes/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkSubtypes();
      setMessage('Subtype deleted successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting subtype:', error);
      setMessage('Error deleting subtype');
      setMessageType('error');
    }
  };

  const handleMoveWorkType = async (typeId, direction) => {
    try {
      const token = localStorage.getItem('access_token');
      const type = workTypes.find(t => t.id === typeId);
      if (!type) {
        console.log('Type not found:', typeId);
        return;
      }

      console.log('Moving type:', type.name, 'direction:', direction);

      // Get all types in the same category
      const categoryTypes = workTypes
        .filter(t => t.category === type.category)
        .sort((a, b) => a.order - b.order);

      console.log('Category types:', categoryTypes.map(t => ({ name: t.name, order: t.order })));

      const currentIndex = categoryTypes.findIndex(t => t.id === typeId);
      if (currentIndex === -1) {
        console.log('Current index not found');
        return;
      }

      // Check if move is possible
      if (direction === 'up' && currentIndex === 0) {
        console.log('Already at top');
        return;
      }
      if (direction === 'down' && currentIndex === categoryTypes.length - 1) {
        console.log('Already at bottom');
        return;
      }

      // Swap orders
      const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      const currentOrder = categoryTypes[currentIndex].order;
      const swapOrder = categoryTypes[swapIndex].order;

      console.log(`Swapping: ${categoryTypes[currentIndex].name} (order ${currentOrder}) <-> ${categoryTypes[swapIndex].name} (order ${swapOrder})`);

      // Update both types
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/work-types/${categoryTypes[currentIndex].id}/`,
        { order: swapOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/work-types/${categoryTypes[swapIndex].id}/`,
        { order: currentOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Successfully swapped, refreshing...');
      fetchWorkTypes();
    } catch (error) {
      console.error('Error moving work type:', error);
      console.error('Error details:', error.response?.data);
      setMessage('Error reordering work type');
      setMessageType('error');
    }
  };

  if (loading || loadingWorkTypes || loadingVisibility) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-graphite-600 text-sm font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-2 bg-steel-100 p-1 rounded-xl w-fit">
        <button
          className={`h-9 px-4 rounded-lg font-bold uppercase tracking-wide text-[11px] transition-all flex items-center gap-2 ${
            activeTab === 'calculation'
              ? 'bg-white text-graphite-900 shadow-sm'
              : 'text-graphite-600 hover:text-graphite-900'
          }`}
          onClick={() => setActiveTab('calculation')}
        >
          <Calculator size={14} />
          Calculation Settings
        </button>
        <button
          className={`h-9 px-4 rounded-lg font-bold uppercase tracking-wide text-[11px] transition-all flex items-center gap-2 ${
            activeTab === 'work-types'
              ? 'bg-white text-graphite-900 shadow-sm'
              : 'text-graphite-600 hover:text-graphite-900'
          }`}
          onClick={() => setActiveTab('work-types')}
        >
          <Settings size={14} />
          Work Types
        </button>
        <button
          className={`h-9 px-4 rounded-lg font-bold uppercase tracking-wide text-[11px] transition-all flex items-center gap-2 ${
            activeTab === 'visibility'
              ? 'bg-white text-graphite-900 shadow-sm'
              : 'text-graphite-600 hover:text-graphite-900'
          }`}
          onClick={() => setActiveTab('visibility')}
        >
          <Eye size={14} />
          Visibility
        </button>
      </div>

      {/* Alert Message */}
      {message && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium ${
          messageType === 'success'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Calculation Settings Tab */}
      {activeTab === 'calculation' && (
        <form onSubmit={handleSave} className="space-y-4">
          {/* Calculation Rules Card */}
          <div className="bg-white rounded-xl border border-steel-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
              <h3 className="text-sm font-bold text-graphite-900">Calculation Rules</h3>
              <p className="text-xs text-graphite-500">Configure work hour calculation settings</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Night Shift Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-graphite-700 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                    Night Shift
                  </h4>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Start Time</label>
                    <input
                      type="time"
                      value={settings.night_shift_start_time}
                      onChange={(e) => handleChange('night_shift_start_time', e.target.value)}
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">End Time</label>
                    <input
                      type="time"
                      value={settings.night_shift_end_time}
                      onChange={(e) => handleChange('night_shift_end_time', e.target.value)}
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                </div>

                {/* Long Rotation Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-graphite-700 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    Long Rotation
                  </h4>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Threshold (hours)</label>
                    <input
                      type="number"
                      value={settings.long_rotation_threshold_hours}
                      onChange={(e) => handleChange('long_rotation_threshold_hours', parseFloat(e.target.value))}
                      min="0"
                      step="0.5"
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <p className="text-xs text-graphite-400">Min. hours for long rotation</p>
                  </div>
                </div>

                {/* Overtime Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-graphite-700 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    Overtime
                  </h4>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Threshold (hours)</label>
                    <input
                      type="number"
                      value={settings.overtime_threshold_hours}
                      onChange={(e) => handleChange('overtime_threshold_hours', parseFloat(e.target.value))}
                      min="0"
                      step="0.5"
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Rate (%)</label>
                    <input
                      type="number"
                      value={settings.overtime_rate_percentage}
                      onChange={(e) => handleChange('overtime_rate_percentage', parseFloat(e.target.value))}
                      min="0"
                      step="0.01"
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <p className="text-xs text-graphite-400">e.g., 150 for 1.5x pay</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Time Discrepancy Detection Card */}
          <div className="bg-white rounded-xl border border-steel-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
              <h3 className="text-sm font-bold text-graphite-900">Time Discrepancy Detection</h3>
              <p className="text-xs text-graphite-500">Configure thresholds for detecting discrepancies between QR check-in/out and timesheet entries</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Low Severity */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                    Low Severity
                  </h4>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Threshold (minutes)</label>
                    <input
                      type="number"
                      value={settings.discrepancy_threshold_low_minutes}
                      onChange={(e) => handleChange('discrepancy_threshold_low_minutes', parseInt(e.target.value))}
                      min="1"
                      step="1"
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <p className="text-xs text-graphite-400">Min. difference to show warning</p>
                  </div>
                </div>

                {/* Medium Severity */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-orange-600 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                    Medium Severity
                  </h4>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Threshold (minutes)</label>
                    <input
                      type="number"
                      value={settings.discrepancy_threshold_medium_minutes}
                      onChange={(e) => handleChange('discrepancy_threshold_medium_minutes', parseInt(e.target.value))}
                      min="1"
                      step="1"
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <p className="text-xs text-graphite-400">Difference for medium alert</p>
                  </div>
                </div>

                {/* High Severity */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-red-600 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    High Severity
                  </h4>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-graphite-600">Threshold (minutes)</label>
                    <input
                      type="number"
                      value={settings.discrepancy_threshold_high_minutes}
                      onChange={(e) => handleChange('discrepancy_threshold_high_minutes', parseInt(e.target.value))}
                      min="1"
                      step="1"
                      className="w-full h-9 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <p className="text-xs text-graphite-400">Difference for high alert</p>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>How it works:</strong> When QR check-in/out times differ from timesheet entries, warnings are shown based on these thresholds.
                  Example: If Low=5, Medium=15, High=30, then 7 minutes difference shows low warning, 20 minutes shows medium, and 35 minutes shows high.
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="h-10 px-6 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold uppercase tracking-wide text-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={14} />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Work Types Tab */}
      {activeTab === 'work-types' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-graphite-900">Work Type Categories</h2>
              <p className="text-xs text-graphite-500">Manage work categories and types</p>
            </div>
            <button
              className="h-9 px-4 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold uppercase tracking-wide text-[10px] transition-all flex items-center gap-1.5"
              onClick={() => {
                setEditingCategory(null);
                setShowCategoryModal(true);
              }}
            >
              <Plus size={14} />
              Add Category
            </button>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {categories.map(category => (
              <div key={category.id} className="bg-white rounded-xl border border-steel-200 shadow-sm overflow-hidden">
                {/* Category Header */}
                <div
                  className="px-4 py-3 border-b flex items-center justify-between"
                  style={{ borderLeftWidth: '4px', borderLeftColor: category.color }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{category.icon || '📁'}</span>
                    <h3 className="font-bold text-graphite-900">{category.name}</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      className="p-1.5 text-graphite-400 hover:text-graphite-600 hover:bg-steel-100 rounded-lg transition-colors"
                      onClick={() => {
                        setEditingCategory(category);
                        setShowCategoryModal(true);
                      }}
                      title="Edit category"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="p-1.5 text-graphite-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      onClick={() => handleDeleteCategory(category.id)}
                      title="Delete category"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Work Types List */}
                <div className="p-3 space-y-2">
                  {workTypes
                    .filter(type => type.category === category.id)
                    .sort((a, b) => a.order - b.order)
                    .map((type, index, filteredTypes) => {
                      const typeSubtypes = workSubtypes.filter(st => st.workType === type.id);
                      return (
                        <div key={type.id} className="space-y-1">
                          {/* Work Type Item */}
                          <div className="flex items-center justify-between p-2 bg-steel-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: type.color || category.color }}
                              ></span>
                              <div>
                                <div className="text-sm font-medium text-graphite-900">{type.name}</div>
                                <div className="text-xs text-graphite-500">
                                  {type.rateModifierType === 'percentage' && type.rateModifierValue !== 0
                                    ? `${type.rateModifierValue}%`
                                    : type.rateModifierType === 'fixed_amount'
                                    ? `+${type.rateModifierValue} PLN`
                                    : '100%'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5">
                              <button
                                className="p-1 text-graphite-400 hover:text-graphite-600 disabled:opacity-30"
                                onClick={() => handleMoveWorkType(type.id, 'up')}
                                disabled={index === 0}
                                title="Move up"
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                className="p-1 text-graphite-400 hover:text-graphite-600 disabled:opacity-30"
                                onClick={() => handleMoveWorkType(type.id, 'down')}
                                disabled={index === filteredTypes.length - 1}
                                title="Move down"
                              >
                                <ChevronDown size={14} />
                              </button>
                              <button
                                className="p-1 text-graphite-400 hover:text-graphite-600"
                                onClick={() => {
                                  setEditingType(type);
                                  setShowTypeModal(true);
                                }}
                                title="Edit"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                className="p-1 text-graphite-400 hover:text-red-500"
                                onClick={() => handleDeleteWorkType(type.id)}
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Subtypes */}
                          {typeSubtypes.length > 0 && (
                            <div className="ml-6 space-y-1">
                              {typeSubtypes.map(subtype => (
                                <div key={subtype.id} className="flex items-center justify-between py-1 px-2 text-xs text-graphite-600 bg-steel-50/50 rounded">
                                  <span>↳ {subtype.name}</span>
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      className="p-0.5 text-graphite-400 hover:text-graphite-600"
                                      onClick={() => {
                                        setEditingSubtype(subtype);
                                        setSelectedWorkType(type);
                                        setShowSubtypeModal(true);
                                      }}
                                      title="Edit subtype"
                                    >
                                      <Edit2 size={12} />
                                    </button>
                                    <button
                                      className="p-0.5 text-graphite-400 hover:text-red-500"
                                      onClick={() => handleDeleteSubtype(subtype.id)}
                                      title="Delete subtype"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add Subtype Button */}
                          <button
                            className="ml-6 text-xs text-brand-500 hover:text-brand-600 font-medium"
                            onClick={() => {
                              setSelectedWorkType(type);
                              setEditingSubtype(null);
                              setShowSubtypeModal(true);
                            }}
                          >
                            + Add Subtype
                          </button>
                        </div>
                      );
                    })}

                  {/* Add Work Type Button */}
                  <button
                    className="w-full py-2 text-xs text-brand-500 hover:text-brand-600 font-medium border border-dashed border-brand-300 rounded-lg hover:bg-brand-50 transition-colors"
                    onClick={() => {
                      setSelectedCategory(category);
                      setEditingType(null);
                      setShowTypeModal(true);
                    }}
                  >
                    + Add Work Type
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Category Modal */}
          {showCategoryModal && (
            <CategoryModal
              category={editingCategory}
              onSave={handleSaveCategory}
              onClose={() => {
                setShowCategoryModal(false);
                setEditingCategory(null);
              }}
            />
          )}

          {/* Work Type Modal */}
          {showTypeModal && (
            <WorkTypeModal
              workType={editingType}
              categories={categories}
              selectedCategory={selectedCategory}
              onSave={handleSaveWorkType}
              onClose={() => {
                setShowTypeModal(false);
                setEditingType(null);
                setSelectedCategory(null);
              }}
            />
          )}

          {/* Subtype Modal */}
          {showSubtypeModal && (
            <SubtypeModal
              subtype={editingSubtype}
              workType={selectedWorkType}
              onSave={handleSaveSubtype}
              onClose={() => {
                setShowSubtypeModal(false);
                setEditingSubtype(null);
                setSelectedWorkType(null);
              }}
            />
          )}
        </div>
      )}

      {/* Visibility Settings Tab */}
      {activeTab === 'visibility' && (
        <div className="space-y-4">
          {/* Header Card */}
          <div className="bg-white rounded-xl border border-steel-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
              <h3 className="text-sm font-bold text-graphite-900">Timesheet Visibility Settings</h3>
              <p className="text-xs text-graphite-500">Configure how many days back users can view their timesheets</p>
            </div>
            <div className="p-4">
              {/* Search and Bulk Actions */}
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={visibilitySearch}
                    onChange={(e) => setVisibilitySearch(e.target.value)}
                    className="w-full h-9 pl-9 pr-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-graphite-400 h-4 w-4" />
                  {visibilitySearch && (
                    <button
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-graphite-400 hover:text-graphite-600"
                      onClick={() => setVisibilitySearch('')}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {selectedUsers.length > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-graphite-600">{selectedUsers.length} user(s) selected</span>
                    <button
                      className="h-9 px-4 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold uppercase tracking-wide text-[10px] transition-all flex items-center gap-1.5"
                      onClick={() => setShowBulkModal(true)}
                    >
                      <Users size={14} />
                      Bulk Update
                    </button>
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="border border-steel-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-steel-200">
                  <thead className="bg-steel-50">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.length > 0 && selectedUsers.length === visibilitySettings.filter(s =>
                            s.user_name.toLowerCase().includes(visibilitySearch.toLowerCase()) ||
                            s.username.toLowerCase().includes(visibilitySearch.toLowerCase())
                          ).length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="h-4 w-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">User</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Username</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Days Visible</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-100">
                    {(() => {
                      const filteredSettings = visibilitySettings.filter(setting =>
                        setting.user_name.toLowerCase().includes(visibilitySearch.toLowerCase()) ||
                        setting.username.toLowerCase().includes(visibilitySearch.toLowerCase())
                      );

                      if (filteredSettings.length === 0) {
                        return (
                          <tr>
                            <td colSpan="5" className="px-3 py-8 text-center text-sm text-graphite-500">
                              {visibilitySearch ? 'No users found matching your search' : 'No users found'}
                            </td>
                          </tr>
                        );
                      }

                      return filteredSettings.map(setting => (
                        <tr key={setting.id} className="hover:bg-steel-50 transition-colors">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(setting.id)}
                              onChange={(e) => handleSelectUser(setting.id, e.target.checked)}
                              className="h-4 w-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-graphite-900">{setting.user_name}</td>
                          <td className="px-3 py-2 text-sm text-graphite-600">{setting.username}</td>
                          <td className="px-3 py-2">
                            {editingVisibility[setting.id] ? (
                              <input
                                type="number"
                                min="1"
                                max="365"
                                value={editingVisibility[setting.id]}
                                onChange={(e) => setEditingVisibility({
                                  ...editingVisibility,
                                  [setting.id]: e.target.value
                                })}
                                className="w-20 h-8 px-2 border border-brand-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                                autoFocus
                              />
                            ) : (
                              <span className="text-sm text-graphite-700 bg-steel-100 px-2 py-1 rounded">{setting.days_visible} days</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {editingVisibility[setting.id] ? (
                              <div className="flex items-center gap-1">
                                <button
                                  className="h-7 px-3 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 transition-colors"
                                  onClick={() => {
                                    handleUpdateVisibility(setting.id, editingVisibility[setting.id]);
                                    const newEditing = { ...editingVisibility };
                                    delete newEditing[setting.id];
                                    setEditingVisibility(newEditing);
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  className="h-7 px-3 bg-steel-200 text-graphite-700 rounded-lg text-xs font-medium hover:bg-steel-300 transition-colors"
                                  onClick={() => {
                                    const newEditing = { ...editingVisibility };
                                    delete newEditing[setting.id];
                                    setEditingVisibility(newEditing);
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                className="h-7 px-3 bg-brand-500 text-white rounded-lg text-xs font-medium hover:bg-brand-600 transition-colors"
                                onClick={() => setEditingVisibility({
                                  ...editingVisibility,
                                  [setting.id]: setting.days_visible
                                })}
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h4 className="text-sm font-bold text-blue-800 mb-2">Information</h4>
            <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
              <li>Days visible determines how far back users can see their timesheet entries</li>
              <li>Minimum: 1 day, Maximum: 365 days</li>
              <li>Default value for new users is 7 days</li>
              <li>Changes take effect immediately</li>
              <li>Use search to filter users and checkboxes to select multiple users for bulk updates</li>
            </ul>
          </div>

          {/* Bulk Update Modal */}
          {showBulkModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBulkModal(false)}>
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-lg font-bold text-graphite-900 mb-2">Bulk Update Visibility Settings</h2>
                <p className="text-sm text-graphite-600 mb-4">
                  You are about to update {selectedUsers.length} user(s)
                </p>
                <div className="space-y-2 mb-6">
                  <label className="block text-sm font-medium text-graphite-700">Days Visible</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={bulkDays}
                    onChange={(e) => setBulkDays(e.target.value)}
                    className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <p className="text-xs text-graphite-400">Set days visible for all selected users</p>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="h-10 px-4 bg-steel-200 text-graphite-700 rounded-xl font-medium hover:bg-steel-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkUpdate}
                    className="h-10 px-4 bg-brand-500 text-white rounded-xl font-bold hover:bg-brand-600 transition-colors"
                  >
                    Update {selectedUsers.length} User(s)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ category, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    color: category?.color || '#3B82F6',
    icon: category?.icon || '',
    order: category?.order || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const apiData = {
      name: formData.name,
      color: formData.color,
      icon: formData.icon,
      order: parseInt(formData.order) || 0,
      is_active: true,
    };
    onSave(apiData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-graphite-900 mb-4">{category ? 'Edit Category' : 'Add Category'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Category Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Color</label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              className="w-full h-10 border border-steel-300 rounded-lg cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Icon (emoji)</label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="e.g., 💼, 🎓, ✈️"
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 bg-steel-200 text-graphite-700 rounded-xl font-medium hover:bg-steel-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-10 px-4 bg-brand-500 text-white rounded-xl font-bold hover:bg-brand-600 transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Work Type Modal Component
const WorkTypeModal = ({ workType, categories, selectedCategory, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    category: workType?.category || selectedCategory?.id || '',
    name: workType?.name || '',
    code: workType?.code || '',
    color: workType?.color || '',
    description: workType?.description || '',
    rateModifierType: workType?.rateModifierType || 'none',
    rateModifierValue: workType?.rateModifierValue || 0,
    requiresSubtype: workType?.requiresSubtype || false,
    requiresGrouping: workType?.requiresGrouping || false,
    order: workType?.order || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert camelCase to snake_case for API
    const apiData = {
      category: formData.category,
      name: formData.name,
      code: formData.code,
      color: formData.color || '',
      description: formData.description || '',
      rate_modifier_type: formData.rateModifierType,
      rate_modifier_value: parseFloat(formData.rateModifierValue) || 0,
      requires_subtype: formData.requiresSubtype,
      requires_grouping: formData.requiresGrouping,
      order: parseInt(formData.order) || 0,
      is_active: true,
    };
    onSave(apiData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-graphite-900 mb-4">{workType ? 'Edit Work Type' : 'Add Work Type'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: parseInt(e.target.value) })}
              required
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Work Type Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Work Day, Work Night"
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Code (unique identifier)</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              required
              placeholder="e.g., work_day"
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Color (optional)</label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              className="w-full h-10 border border-steel-300 rounded-lg cursor-pointer"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Rate Modifier Type</label>
            <select
              value={formData.rateModifierType}
              onChange={(e) => setFormData({ ...formData, rateModifierType: e.target.value })}
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
            >
              <option value="none">None (100% base rate)</option>
              <option value="percentage">Percentage</option>
              <option value="fixed_amount">Fixed Amount (PLN)</option>
            </select>
          </div>

          {formData.rateModifierType !== 'none' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-graphite-700">
                {formData.rateModifierType === 'percentage'
                  ? 'Percentage (e.g., 150 for 150%)'
                  : 'Amount (PLN)'}
              </label>
              <input
                type="number"
                value={formData.rateModifierValue}
                onChange={(e) => setFormData({ ...formData, rateModifierValue: parseFloat(e.target.value) })}
                step="0.01"
                min="0"
                className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-3 py-2 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requiresSubtype}
                onChange={(e) => setFormData({ ...formData, requiresSubtype: e.target.checked })}
                className="h-4 w-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
              />
              <span className="text-sm text-graphite-700">Requires subtype selection</span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requiresGrouping}
                onChange={(e) => setFormData({ ...formData, requiresGrouping: e.target.checked })}
                className="h-4 w-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
              />
              <span className="text-sm text-graphite-700">Group entries in management view</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 bg-steel-200 text-graphite-700 rounded-xl font-medium hover:bg-steel-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-10 px-4 bg-brand-500 text-white rounded-xl font-bold hover:bg-brand-600 transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Subtype Modal Component
function SubtypeModal({ subtype, workType, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: subtype?.name || '',
    code: subtype?.code || '',
    description: subtype?.description || '',
    order: subtype?.order || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const apiData = {
      work_type: workType?.id,
      name: formData.name,
      code: formData.code,
      description: formData.description || '',
      order: parseInt(formData.order) || 0,
      is_active: true,
    };
    onSave(apiData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-graphite-900 mb-2">{subtype ? 'Edit Subtype' : 'Add Subtype'}</h2>
        {workType && (
          <p className="text-sm text-graphite-500 mb-4">
            For work type: <strong>{workType.name}</strong>
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Subtype Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Email handling, Documentation"
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Code (unique identifier)</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              required
              placeholder="e.g., email_handling"
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Optional description"
              className="w-full px-3 py-2 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-graphite-700">Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              className="w-full h-10 px-3 border border-steel-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 bg-steel-200 text-graphite-700 rounded-xl font-medium hover:bg-steel-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-10 px-4 bg-brand-500 text-white rounded-xl font-bold hover:bg-brand-600 transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TimesheetSettings;
